import { ReactNode } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Button } from "@gogoro/go-design"
import { yupResolver } from "@hookform/resolvers/yup"
import { AxiosError, isAxiosError } from "axios"
import { useAuth } from "customerAttachments/auth/AuthProvider"
import { LoginReq } from "customerAttachments/auth/AuthProvider/api"
import { useModal } from "customerAttachments/contexts/ModalProvider"
import { salesMessageCodeMap } from "modules/messageCodeSales"
import { GogoroSvg } from "views/components/Icon"

import FormField from "components/FormField"

import schema from "./validations"

import styles from "./styles.module.scss"

const DescriptionFragment = ({ children }: { children?: ReactNode }) => {
  return <span>{children}</span>
}

const PrivatePolicyLink = ({ children }: { children?: ReactNode }) => {
  return (
    <a href="https://www.gogoro.com/tw/privacy-policy/" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  )
}

const LoginPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation(["sales", "label", "authentication"])
  const { login } = useAuth() || {}
  const { openModal } = useModal()

  const form = useForm({
    shouldFocusError: true,
    resolver: yupResolver(schema({ t })),
  })

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form

  const onSubmit = () => {
    return handleSubmit(
      (payload) => {
        login &&
          login(payload as LoginReq)
            .then(() => navigate("/"))
            .catch((e: Error | AxiosError) => {
              if (isAxiosError(e)) {
                const data = e.response?.data
                const { MessageCode } = data
                let transKey = salesMessageCodeMap.get(parseInt(MessageCode))
                transKey = transKey ? `sales:message.${transKey}` : JSON.stringify(data)
                openModal({
                  title: t("customer_attachments.warning"),
                  content: t(transKey),
                  showCancel: false,
                  showClose: false,
                })
              }

              console.error(e)
            })
      },
      (error) => {
        console.error(error)
      },
    )()
  }
  return (
    <div className={styles.container}>
      <div className={styles.logo}>
        <GogoroSvg className="" />
      </div>
      <h4 className={styles.title}>{t("sales:customer_attachments.private_information_file_upload")}</h4>
      <FormProvider {...form}>
        <div className={styles["form-container"]}>
          <FormField
            label={t("label:email")}
            name="account"
            placeholder={t("authentication:prompt_account")}
            disabled={isSubmitting}
          />
          <FormField
            type="password"
            label={t("mobile.password")}
            name="password"
            placeholder={t("authentication:prompt_password")}
            disabled={isSubmitting}
          />
        </div>
      </FormProvider>
      <Button className={styles["submit-btn"]} onClick={onSubmit} disabled={isSubmitting}>
        {t("label:login")}
      </Button>
      <div className={styles["disclaimer-container"]}>
        <div className={styles["disclaimer-section"]}>
          <Trans
            i18nKey="sales:customer_attachments.login_description"
            components={{ a: <PrivatePolicyLink />, span: <DescriptionFragment /> }}
          />
        </div>
      </div>
    </div>
  )
}

export default LoginPage
